import type { ErrorHandlingSagaDetail, ErrorHandlingSagaPagedGrid, ErrorHandlingSagaPagedQuery, HttpRequestLogData } from '@/dtos/ErrorHandlingSaga'
import type { SagaWorkflowGraphDto } from '@/dtos/SagaWorkflow'
import apiClient from 'BrokerTsLib/src/Utils/apiClient'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useUrl } from '.'
import { Apis } from '../appSettings/AppSettings'

export const useGetErrors = () => {
  const url = useUrl(Apis.errorHandlingSagas)

  return useMutation({
    mutationFn: async (filter: ErrorHandlingSagaPagedQuery) => {
      const response = await apiClient.post<ErrorHandlingSagaPagedGrid>(`${url}/GetErrors`, filter)
      return response?.data
    },
  })
}

export const useGetError = (id?: string) => {
  const url = useUrl(Apis.errorHandlingSagas)

  return useQuery({
    queryKey: ['getError', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await apiClient.get<ErrorHandlingSagaDetail>(`${url}/GetError?id=${id}`)
      return res.data
    },
    retry: false,
  })
}

// Initial workflow-graph snapshot for a saga; live deltas then stream over the saga-workflow hub.
export const useGetWorkflow = (rootId?: string) => {
  const url = useUrl(Apis.errorHandlingSagas)

  return useQuery({
    queryKey: ['getWorkflow', rootId],
    enabled: !!rootId,
    queryFn: async () => {
      const res = await apiClient.get<SagaWorkflowGraphDto>(`${url}/GetWorkflow?id=${rootId}`)
      return res.data
    },
    retry: false,
  })
}

// Lazily fetches the request/response payload of one "Logy" row; enabled only once a row is expanded.
export const useGetLogData = (logId?: number) => {
  const url = useUrl(Apis.errorHandlingSagas)

  return useQuery({
    queryKey: ['getLogData', logId],
    enabled: logId != null,
    queryFn: async () => {
      const res = await apiClient.get<HttpRequestLogData>(`${url}/GetLogData?logId=${logId}`)
      return res.data
    },
    retry: false,
  })
}

const useAction = <TBody>(action: string) => {
  const url = useUrl(Apis.errorHandlingSagas)

  return useMutation({
    mutationFn: async (body: TBody) => {
      await apiClient.post(`${url}/${action}`, body)
    },
  })
}

export const useTakeOver = () => useAction<{ ids: string[] }>('TakeOver')
export const useAssign = () => useAction<{ ids: string[]; assigneeId?: string | null }>('Assign')
export const useSetNote = () => useAction<{ id: string; note?: string | null }>('SetNote')
export const useRetry = () => useAction<{ ids: string[] }>('Retry')
export const useResolve = () => useAction<{ ids: string[] }>('Resolve')
export const useDiscard = () => useAction<{ ids: string[]; reason: string }>('Discard')
export const useCancel = () => useAction<{ ids: string[]; reason?: string }>('Cancel')
